import React, { useEffect, useMemo, useState } from 'react';

import { getAllBreakers } from 'utils/BreakerAPI';
import { Attribute, Breaker } from 'types/Breaker';
import debounce from 'lodash/debounce';

export type AttributeMap = {
  [key: string]: Set<string>;
};

const shuffle = (breakers: Breaker[]) => {
  for (let i = breakers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [breakers[i], breakers[j]] = [breakers[j], breakers[i]];
  }
};

const pageSize = 24;

export default function useBreakerGallery() {
  const [isLoading, setIsLoading] = useState(true);
  const [allBreakers, setAllBreakers] = useState<Breaker[]>([]);
  const [index, setIndex] = useState(0);
  const [attributeMap, setAttributeMap] = useState<AttributeMap>({});
  const [activeFilters, setActiveFilters] = useState<Attribute[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const allBreakers = await getAllBreakers();

      let newAttributeMap: AttributeMap = {};
      allBreakers.forEach(({ attributes }) => {
        for (const { traitType, value } of attributes) {
          const values = newAttributeMap[traitType];

          if (!values) {
            newAttributeMap[traitType] = new Set([value]);
          } else {
            newAttributeMap[traitType] = values.add(value);
          }
        }
      });

      shuffle(allBreakers);

      setAttributeMap(newAttributeMap);
      setAllBreakers(allBreakers);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  let breakers;

  if (activeFilters.length === 0) {
    breakers = allBreakers;
  } else {
    breakers = allBreakers.filter((breaker) => {
      let filterGroups: { [k: string]: string[] } = {};

      activeFilters.forEach(({ traitType, value }) => {
        if (filterGroups[traitType]) {
          filterGroups[traitType].push(value);
        } else {
          filterGroups[traitType] = [value];
        }
      });

      for (const [traitType, values] of Object.entries(filterGroups)) {
        const attribute = breaker.attributes.find((attribute) => attribute.traitType === traitType);

        if (!attribute || !values.includes(attribute.value)) {
          return false;
        }
      }

      return true;
    });
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((e) => {
        // call API here
        console.log(e);
      }, 300),
    [],
  );

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    debouncedSearch(e);
  };

  const setFilter = ({ traitType, value }: Attribute) => {
    setActiveFilters([
      ...activeFilters,
      {
        traitType,
        value,
      },
    ]);

    setIndex(0);
  };

  const removeFilter = ({ traitType, value }: Attribute) => {
    setActiveFilters(
      activeFilters.filter((filter) => {
        return filter.traitType !== traitType || filter.value !== value;
      }),
    );

    setIndex(0);
  };

  const clearFilters = () => {
    setActiveFilters([]);
    setIndex(0);
  };

  const count = breakers.length;

  const loadMore =
    (index + 1) * pageSize <= count
      ? () => {
          setIndex(index + 1);
        }
      : null;

  const getBreakerById = (id: number | null) => {
    return allBreakers.find((breaker) => breaker.id === id);
  };

  return {
    isLoading,
    attributeMap,
    activeFilters,
    setFilter,
    removeFilter,
    clearFilters,
    loadMore,
    getBreakerById,
    count,
    breakers: breakers.slice(0, pageSize * (index + 1)),
    onSearch,
    setSearch,
    search,
  };
}
